import dayjs from 'dayjs'
import { SaveAppointment } from '../models/saveAppointment'
import { AuthenticateUser } from '../models/authentication'
import {
    StructuredResponse,
    Location,
    Procedure,
    Appointment,
    Patient,
    Customization,
    RSVPData,
    Provider,
} from './interfaces'
import { BookingStep, PatientType, Sex } from '../models/enums'
import { ReschedulingConfiguration } from '../models/customization'
import WIDGET_KEY from '../shared/widgetKey'
import { ValidatorPreset } from '../shared/validator'
import { DATE_FORMAT } from '../shared/const'

interface PatientSearch {
    first_name: string
    last_name: string
    birth_date: string
    location_id: string
}

interface AvailableTimesRange {
    from: string
    to: string
    durationInSeconds?: number
    providerId?: string
}

async function customFetch(endpoint: string, customOptions: RequestInit = {}, queries: Record<string, any> = {}) {
    const options = {
        mode: 'cors' as const,
        headers: new Headers({ 'content-type': 'application/json' }),
        ...customOptions,
    }
    let url = `${process.env.PREACT_APP_SIMPLIFEYE_API_URL}/${endpoint}`

    if (queries && Object.keys(queries).length > 0) {
        url +=
            '?' +
            Object.entries(queries)
                .filter(([, value]) => Boolean(value))
                .map(([key, value]) => `${key}=${value}`)
                .join('&')
    }

    const response = await fetch(url, options)
    const responseJSON: StructuredResponse<any> = response && (await response.json())

    if (responseJSON?.errors && responseJSON?.errors.length > 0) {
        const errorMessages = responseJSON.errors.map((error) => error?.message || error?.details).join(' ')
        throw Error(errorMessages)
    }
    if (responseJSON?.status === 401) {
        throw Error('Sorry, there was a problem validating your request.')
    }

    return responseJSON
}

async function customFetchAuth(
    endpoint: string,
    token: string,
    customOptions: RequestInit = {},
    queries: Record<string, any> = {}
) {
    const options = {
        mode: 'cors' as const,
        headers: new Headers({ 'content-type': 'application/json', authorization: `Bearer ${token}` }),
        ...customOptions,
    }
    let url = `${process.env.PREACT_APP_SIMPLIFEYE_API_URL}/${endpoint}`

    if (queries && Object.keys(queries).length > 0) {
        url +=
            '?' +
            Object.entries(queries)
                .filter(([, value]) => Boolean(value))
                .map(([key, value]) => `${key}=${value}`)
                .join('&')
    }

    const response = await fetch(url, options)
    const responseJSON: StructuredResponse<any> = response && (await response.json())

    if (responseJSON?.errors && responseJSON?.errors.length > 0) {
        const errorMessages = responseJSON.errors.map((error) => error?.message || error?.details).join(' ')
        throw Error(errorMessages)
    }

    return responseJSON
}

export async function getCustomization(): Promise<StructuredResponse<Customization>> {
    const response = await customFetch(`public/self-scheduling/v1/widget/${WIDGET_KEY}/customization`)

    return response
}

export async function getLocations(): Promise<StructuredResponse<Location[]>> {
    const response = await customFetch(`public/self-scheduling/v1/widget/${WIDGET_KEY}/locations`)

    return response
}

export async function listProcedures(locationId: string): Promise<StructuredResponse<Procedure[]>> {
    const response = await customFetch(
        `public/self-scheduling/v1/widget/${WIDGET_KEY}/location/${locationId}/procedures`
    )

    return response
}

export async function listPatients(search: PatientSearch): Promise<StructuredResponse<Patient[]>> {
    const response = await customFetch(`public/self-scheduling/v1/widget/${WIDGET_KEY}/patient`, {}, search)

    return response
}

export async function postAppointment(
    locationId: string,
    procedureId: string,
    payload: SaveAppointment
): Promise<StructuredResponse<Appointment>> {
    const response = await customFetch(
        `public/self-scheduling/v1/widget/${WIDGET_KEY}/location/${locationId}/procedure/${procedureId}/appointment`,
        {
            method: 'POST',
            body: JSON.stringify(payload),
        }
    )

    return response
}

export async function listAvailableTimes(
    locationId: string,
    procedureId: string,
    searchRange: AvailableTimesRange
): Promise<StructuredResponse<string[]>> {
    const response = await customFetch(
        `public/self-scheduling/v1/widget/${WIDGET_KEY}/location/${locationId}/procedure/${procedureId}/availability`,
        {},
        searchRange
    )

    return response
}

export async function getChairfillData(chairfillId: string, patientPmsId?: string, patientId?: string) {
    const queriesObj: {
        chairfillId: string
        patientId?: string
        patientPmsId?: string
    } = { chairfillId }

    if (patientPmsId) {
        queriesObj['patientPmsId'] = patientPmsId
    }

    if (patientId && !patientPmsId) {
        queriesObj['patientId'] = patientId
    }

    const { data } = (await customFetch(
        `public/self-scheduling/v1/widget/${WIDGET_KEY}/rsvp`,
        {},
        queriesObj
    )) as StructuredResponse<RSVPData>

    if (data.isAppointmentScheduled) {
        return {
            practiceName: data.practice.name,
            websiteURL: data.selfSchedulingWebCode.url,
            locationId: data.locationId,
            isAppointmentScheduled: data.isAppointmentScheduled,
        }
    }
    if (!data.procedures) {
        data.procedures = []
    }
    const hasDefaultProcedures = data.procedures.length > 0
    const reasonOfAppointment = {
        isValid: hasDefaultProcedures ? false : true,
        isRequired: true,
        validators: [],
        value: hasDefaultProcedures ? '' : data.procedureId,
        isDirty: hasDefaultProcedures ? false : true,
    }
    return {
        errorMessage: '',
        bookingStep: BookingStep.DETAILS,
        patientDetails: {
            firstName: {
                isValid: false,
                isRequired: true,
                validators: [ValidatorPreset.NAME],
                value: '',
                isDirty: false,
            },
            lastName: {
                isValid: false,
                isRequired: true,
                validators: [ValidatorPreset.NAME],
                value: '',
                isDirty: false,
            },
            dateOfBirth: {
                isValid: false,
                isRequired: true,
                validators: [ValidatorPreset.BIRTH_DATE],
                value: '',
                isDirty: false,
            },
            emailAddress: {
                isValid: false,
                isRequired: true,
                validators: [ValidatorPreset.EMAIL_ADDRESS],
                value: '',
                isDirty: false,
            },
            phone: {
                isValid: false,
                isRequired: true,
                validators: [ValidatorPreset.PHONE_NUMBER],
                value: '',
                isDirty: false,
            },
            sex: {
                isValid: true,
                isRequired: false,
                validators: [ValidatorPreset.SEX],
                value: undefined,
                isDirty: true,
            },
            reasonOfAppointment,
            provider: {
                isValid: true,
                isRequired: false,
                validators: [],
                value: undefined,
                isDirty: true,
            },
            hasInsurance: {
                isValid: false,
                isRequired: false,
                validators: [],
                value: undefined,
                isDirty: true,
            },
        },
        selectedLocationId: data.locationId,
        selectedAppointmentDateRangeFrom: dayjs().toString(),
        selectedAppointmentDateRangeTo: dayjs().add(1, 'month').date(1).toString(),
        treatmentLength: data.treatmentLength,
        patientType: PatientType.EXISTING,
        primaryProviderId: data.primaryProvider?.id,
        schedulingProcedures: data.schedulingProcedures,
        procedures: data.procedures,
    }
}

export async function listProcedureProviders(
    locationId: string,
    procedureId: string
): Promise<StructuredResponse<Provider[]>> {
    const response = await customFetch(
        `public/self-scheduling/v1/widget/${WIDGET_KEY}/location/${locationId}/procedure/${procedureId}/provider`,
        {}
    )

    return response
}

export async function getLocationInsuranceSettings(locationId: string) {
    const response = await customFetch(
        `v2/api/ssw/${WIDGET_KEY}/practice-locations/${locationId}/settings/scheduling-insurance-provider`,
        {}
    )

    return response
}

export async function listLocationInsuranceProviders(locationId: string) {
    const response = await customFetch(
        `v2/api/ssw/${WIDGET_KEY}/patients/insurance-providers?locationId=${locationId}`,
        {}
    )
    return response
}

export async function getLocationById(locationId: string) {
    const response = await customFetch(`v2/api/ssw/practice-locations/${locationId}`, {})
    return response
}

export async function postAuthenticateUser(
    locationId: string,
    payload: AuthenticateUser
): Promise<StructuredResponse<{ jwt_token: string }>> {
    const response = await customFetch(`v2/api/ssw/practice-locations/${locationId}/authenticate`, {
        method: 'POST',
        body: JSON.stringify(payload),
    })
    return response
}

export async function postCancelAppointment(
    locationId: string,
    reason: string,
    token: string
): Promise<StructuredResponse<any>> {
    const response = await customFetchAuth(
        `v2/api/ssw/practice-locations/${locationId}/appointment-tasks/cancel`,
        token,
        {
            method: 'POST',
            body: JSON.stringify({ reason }),
        }
    )
    const status = response.status || response.statusCode

    if (status && parseInt(String(status)) > 299) {
        throw Error('Failed to cancel appointment')
    }

    return response
}

export async function postConfirmAppointment(locationId: string, token: string): Promise<StructuredResponse<any>> {
    const response = await customFetchAuth(
        `v2/api/ssw/practice-locations/${locationId}/appointment-tasks/confirm`,
        token,
        {
            method: 'POST',
        }
    )
    return response
}

export async function postRescheduleAppointment(
    locationId: string,
    transactionId: string,
    appointmentTime: string,
    token: string
): Promise<StructuredResponse<any>> {
    const response = await customFetchAuth(
        `v2/api/ssw/practice-locations/${locationId}/appointment-tasks/reschedule`,
        token,
        {
            method: 'POST',
            body: JSON.stringify({ datetime: appointmentTime, transactionId }),
        }
    )
    return response
}

export async function listAvailableReschedulingTimes(
    locationId: string,
    from: string,
    to: string,
    token: string
): Promise<StructuredResponse<string[]>> {
    const response = await customFetchAuth(
        `v2/api/ssw/practice-locations/${locationId}/appointment-tasks/availabilities`,
        token,
        {},
        {
            start_date: from,
            end_date: to,
        }
    )
    return response
}

export async function getLocationReschedulingConfiguration(
    locationId: string,
    token: string
): Promise<StructuredResponse<ReschedulingConfiguration>> {
    const response = await customFetchAuth(
        `v2/api/ssw/practice-locations/${locationId}/rescheduling-configuration`,
        token
    )
    return response
}
